import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  useMediaQuery,
} from '@mui/material';
import {
  TouchApp as KioskIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Computer as ComputerIcon,
  LocationOn as LocationIcon,
  Inventory as InventoryIcon,
  RestartAlt as RestartAltIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { storage } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { mainApi, remoteApi } from '../../config/apiClient';
import firebaseDataService from '../../services/FirebaseDataService';
import { uploadBytes, getDownloadURL, ref as storageRef } from 'firebase/storage';
import AddKioskModal from './AddKioskModal';
import KioskSettingsModal from './KioskSettingsModal';

// Kiosk veri tipi
interface KioskData {
  id: string;
  name: string;
  description?: string;
  branchId?: string | null;
  branchName?: string;
  status: 'active' | 'inactive' | 'maintenance';
  screenSize: '21inch' | '24inch' | '27inch';
  deviceId?: string;
  createdAt?: string;
  updatedAt?: string;
  // Cihaz durumu bilgileri
  deviceScreenCount?: number;
  activeDeviceCount?: number;
  lastDeviceSeen?: string;
  screenCode?: string;
  deviceStatus?: 'none' | 'pending' | 'active' | 'inactive';
}

const KioskMenuPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  
  // State tanımlamaları
  const [loading, setLoading] = useState(true);
  const [kiosks, setKiosks] = useState<KioskData[]>([]);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Sayfalama için state'ler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 10 : 25);

  // Menü için state'ler
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedKiosk, setSelectedKiosk] = useState<KioskData | null>(null);
  
  // Modal state'leri
  const [addKioskModalOpen, setAddKioskModalOpen] = useState(false);
  const [editingKiosk, setEditingKiosk] = useState<KioskData | null>(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsKiosk, setSettingsKiosk] = useState<KioskData | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  
  // Snackbar state'leri
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Şirket ID'sini al
  useEffect(() => {
    const fetchCompanyId = async () => {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        setError(t('kioskMenuPage.errors.userNotLoggedIn'));
        setLoading(false);
        return;
      }
      
      try {
        console.log('🔍 [KIOSK] Şirket ID alınıyor...', { userId: uid });
        const userData = await firebaseDataService.getUserData(uid);
        
        if (userData) {
          console.log('✅ [KIOSK] Kullanıcı bilgileri alındı', { 
            companyId: userData.companyId,
            role: userData.role 
          });
          setCompanyId(userData.companyId || null);
        } else {
          setError(t('kioskMenuPage.errors.userInfoNotFound'));
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ [KIOSK] Şirket ID alınırken hata:', error);
        setError(t('kioskMenuPage.errors.companyInfoLoadFailed'));
        setLoading(false);
      }
    };
    
    fetchCompanyId();
  }, []);

  // Kiosk verilerini yükle
  const loadKiosks = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      console.log('🔍 [KIOSK] Kiosk verileri yükleniyor...', { companyId });
      
      const response = await mainApi.get(`/api/Kiosk`);
      const result = response.data;
      if (result.success && result.data) {
        const kioskList: KioskData[] = result.data.map((kiosk: any) => ({
          id: kiosk.id.toString(),
          name: kiosk.name,
          description: kiosk.description,
          branchId: kiosk.branchId,
          branchName: kiosk.branchName,
          status: kiosk.status,
          screenSize: kiosk.screenSize,
          deviceId: kiosk.deviceId,
          createdAt: kiosk.createdAt,
          updatedAt: kiosk.updatedAt,
          deviceScreenCount: kiosk.deviceScreenCount,
          activeDeviceCount: kiosk.activeDeviceCount,
          lastDeviceSeen: kiosk.lastDeviceSeen,
          screenCode: kiosk.screenCode,
          deviceStatus: kiosk.deviceStatus
        }));
        
        setKiosks(kioskList);
        console.log('✅ [KIOSK] Kiosk verileri yüklendi', { count: kioskList.length });
      } else {
        console.log('ℹ️ [KIOSK] Henüz kiosk bulunamadı');
        setKiosks([]);
      }
    } catch (error: any) {
      console.error('❌ [KIOSK] Kiosk verileri yüklenirken hata:', error);
      if (error.response?.status === 400) {
        console.log('ℹ️ [KIOSK] Şirket henüz kayıtlı değil, boş liste gösteriliyor');
        setKiosks([]);
      } else {
        setError(t('kioskMenuPage.errors.kioskLoadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKiosks();
  }, [companyId]);

  // Yardımcı fonksiyonlar
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('kioskMenuPage.status.active');
      case 'inactive':
        return t('kioskMenuPage.status.inactive');
      case 'maintenance':
        return t('kioskMenuPage.status.maintenance');
      default:
        return t('kioskMenuPage.status.unknown');
    }
  };

  const getScreenSizeText = (size: string) => {
    switch (size) {
      case '21inch':
        return t('kioskMenuPage.screenSize.21inch');
      case '24inch':
        return t('kioskMenuPage.screenSize.24inch');
      case '27inch':
        return t('kioskMenuPage.screenSize.27inch');
      default:
        return size;
    }
  };

  // Event handlers
  const handleAddKiosk = () => {
    console.log('🆕 [KIOSK] Yeni kiosk ekleme modalı açılıyor');
    setAddKioskModalOpen(true);
  };

  const handleKioskAdded = () => {
    console.log('🔄 [KIOSK] Yeni kiosk eklendi, liste yenileniyor');
    loadKiosks();
  };

  // Firebase Storage'a resim yükleme fonksiyonu
  const handleImageUpload = async (productId: string, file: File): Promise<string> => {
    if (!companyId || !settingsKiosk?.id) {
      throw new Error(t('kioskMenuPage.errors.companyOrKioskMissing'));
    }

    try {
      console.log('🔄 [KIOSK] Firebase Storage\'a resim yükleniyor...', { productId, fileName: file.name });
      
      // Benzersiz dosya adı oluştur
      const timestamp = Date.now();
      const fileName = `${productId}_${timestamp}_${file.name}`;
      const filePath = `companies/${companyId}/kiosks/${settingsKiosk.id}/images/${fileName}`;
      
      // Firebase Storage referansı oluştur
      const fileRef = storageRef(storage, filePath);
      
      // Metadata ile dosyayı yükle
      const metadata = {
        contentType: file.type || 'image/jpeg',
        cacheControl: 'public,max-age=31536000', // 1 yıl cache
        customMetadata: {
          uploadedBy: 'kiosk-settings',
          uploadedAt: new Date().toISOString(),
          originalFileName: file.name,
          productId: productId
        }
      };
      
      await uploadBytes(fileRef, file, metadata);
      console.log('✅ [KIOSK] Dosya Firebase Storage\'a yüklendi');
      
      // Download URL al
      const downloadURL = await getDownloadURL(fileRef);
      console.log('✅ [KIOSK] Download URL alındı:', downloadURL);
      
      return downloadURL;
    } catch (error) {
      console.error('❌ [KIOSK] Firebase Storage yükleme hatası:', error);
      throw error;
    }
  };

  const handleEditKiosk = (kiosk: KioskData) => {
    console.log('✏️ [KIOSK] Kiosk düzenleme:', kiosk);
    setEditingKiosk(kiosk);
    setAddKioskModalOpen(true);
  };

  const handleSettingsKiosk = (kiosk: KioskData) => {
    console.log('⚙️ [KIOSK] Kiosk ayarları:', kiosk);
    setSettingsKiosk(kiosk);
    setSettingsModalOpen(true);
  };

  const handleViewKiosk = (kiosk: KioskData) => {
    console.log('👁️ [KIOSK] Kiosk görüntüleme:', kiosk);
    // TODO: Kiosk önizleme sayfası açılacak
  };

  const handleDeleteKiosk = (kiosk: KioskData) => {
    console.log('🗑️ [KIOSK] Kiosk silme:', kiosk);
    setSelectedKiosk(kiosk);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedKiosk) return;

    try {
      setLoading(true);
      
      await mainApi.post(`/api/Kiosk/${selectedKiosk.id}/delete`);

      setSnackbar({
        open: true,
        message: t('kioskMenuPage.messages.deleteSuccess'),
        severity: 'success'
      });

      // Listeyi yenile
      await loadKiosks();
    } catch (err) {
      console.error('❌ [KIOSK] Kiosk silme hatası:', err);
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : t('kioskMenuPage.errors.deleteFailed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setSelectedKiosk(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedKiosk(null);
  };

  // Menü açma
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, kiosk: KioskData) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedKiosk(kiosk);
  };

  // Menü kapatma
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedKiosk(null);
  };

  // Menüden düzenleme
  const handleEditFromMenu = () => {
    if (selectedKiosk) {
      handleEditKiosk(selectedKiosk);
    }
    handleMenuClose();
  };

  // Menüden ayarlar
  const handleSettingsFromMenu = () => {
    if (selectedKiosk) {
      handleSettingsKiosk(selectedKiosk);
    }
    handleMenuClose();
  };


  // Menüden silme
  const handleDeleteFromMenu = () => {
    if (selectedKiosk) {
      handleDeleteKiosk(selectedKiosk);
    }
    handleMenuClose();
  };

  // Menüden kiosk cihazlarını yeniden başlat - onay modalı aç
  const handleRestartFromMenu = () => {
    if (!selectedKiosk || !companyId) return;
    const kioskToRestart = selectedKiosk;
    handleMenuClose();
    setSelectedKiosk(kioskToRestart);
    setRestartDialogOpen(true);
  };

  const handleRestartConfirm = async () => {
    if (!selectedKiosk || !companyId) return;

    setRestartDialogOpen(false);

    try {
      const response = await remoteApi.post('/api/kiosk-management/restart-by-branch', {
        assignedCompanyId: companyId,
        assignedBranchId: selectedKiosk.branchId || null
      });

      const result = response.data;

      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
      } else {
        setSnackbar({ open: true, message: result.message, severity: 'warning' });
      }
    } catch (error) {
      console.error('Kiosk yeniden başlatma hatası:', error);
      setSnackbar({ open: true, message: t('kioskMenuPage.errors.restartRequestFailed'), severity: 'error' });
    } finally {
      setSelectedKiosk(null);
    }
  };

  const handleRestartCancel = () => {
    setRestartDialogOpen(false);
    setSelectedKiosk(null);
  };

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sayfalandırılmış kiosk verileri
  const paginatedKiosks = kiosks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('kioskMenuPage.loading')}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          {t('kioskMenuPage.actions.refreshPage')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      <Helmet>
        <title>{t('kioskMenuPage.documentTitle')}</title>
      </Helmet>

      {/* Başlık */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Box display="flex" alignItems="center">
          <KioskIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.main, mr: 2 }} />
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            {t('kioskMenuPage.title')}
          </Typography>
        </Box>
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<InventoryIcon />}
            size="large"
            sx={{ 
              borderRadius: 2, 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              padding: { xs: '8px 16px', sm: '10px 22px' }
            }}
            onClick={() => navigate('/kiosk-products')}
          >
            {t('kioskMenuPage.actions.kioskProducts')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ 
              borderRadius: 2, 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              padding: { xs: '8px 16px', sm: '10px 22px' }
            }}
            onClick={handleAddKiosk}
          >
            {t('kioskMenuPage.actions.addKiosk')}
          </Button>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('kioskMenuPage.stats.totalKiosk')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {kiosks.length}
                  </Typography>
                </Box>
                <KioskIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('kioskMenuPage.stats.activeKiosk')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                    {kiosks.filter(k => k.status === 'active').length}
                  </Typography>
                </Box>
                <KioskIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('kioskMenuPage.stats.maintenance')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                    {kiosks.filter(k => k.status === 'maintenance').length}
                  </Typography>
                </Box>
                <SettingsIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('kioskMenuPage.stats.inactiveKiosk')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="error.main">
                    {kiosks.filter(k => k.status === 'inactive').length}
                  </Typography>
                </Box>
                <KioskIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Kiosk Listesi */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Box p={3}>
          <Typography variant="h6" component="h2" fontWeight="bold" mb={2}>
            {t('kioskMenuPage.list.title', { count: kiosks.length })}
          </Typography>
          
          {kiosks.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('kioskMenuPage.list.emptyState')}
            </Alert>
          ) : (
            <>
              {/* 💻 MASAÜSTÜ: Tablo Görünümü */}
              {!isMobile && (
                <TableContainer component={Paper} sx={{ 
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: '70vh'
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 150
                        }}>{t('kioskMenuPage.table.kiosk')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 80
                        }}>{t('kioskMenuPage.table.status')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 100
                        }}>{t('kioskMenuPage.table.screenSize')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 120
                        }}>{t('kioskMenuPage.table.branch')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 120
                        }}>{t('kioskMenuPage.table.deviceStatus')}</TableCell>
                        <TableCell align="right" sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 80
                        }}>{t('kioskMenuPage.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedKiosks.map((kiosk) => (
                        <TableRow 
                          key={kiosk.id} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            '& .MuiTableCell-root': { 
                              py: 0.5,
                              height: 32,
                              fontSize: '0.775rem'
                            }
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <ComputerIcon sx={{ 
                                fontSize: 20, 
                                mr: 1, 
                                color: theme.palette.primary.main
                              }} />
                              <Typography variant="body2" fontWeight="medium">
                                {kiosk.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusText(kiosk.status)}
                              color={getStatusColor(kiosk.status) as any}
                              size="small"
                              sx={{ fontSize: '11px', height: 24 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {getScreenSizeText(kiosk.screenSize)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{
                              color: !kiosk.branchName || kiosk.branchName === t('kioskMenuPage.table.unassigned') ? 'text.secondary' : 'text.primary'
                            }}>
                              {kiosk.branchName || t('kioskMenuPage.table.unassigned')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {kiosk.deviceStatus === 'active' ? (
                              <Chip 
                                label={t('kioskMenuPage.deviceStatus.connected')} 
                                color="success" 
                                size="small" 
                                sx={{ fontSize: '11px', height: 24 }} 
                              />
                            ) : kiosk.deviceStatus === 'pending' ? (
                              <Chip 
                                label={t('kioskMenuPage.deviceStatus.pendingSetup')} 
                                color="warning" 
                                size="small" 
                                sx={{ fontSize: '11px', height: 24 }} 
                              />
                            ) : kiosk.deviceStatus === 'inactive' ? (
                              <Chip 
                                label={t('kioskMenuPage.deviceStatus.offline')} 
                                color="error" 
                                size="small" 
                                sx={{ fontSize: '11px', height: 24 }} 
                              />
                            ) : (
                              <Chip 
                                label={t('kioskMenuPage.deviceStatus.noDevice')} 
                                variant="outlined"
                                size="small" 
                                sx={{ fontSize: '11px', height: 24, color: 'text.secondary' }} 
                              />
                            )}
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, kiosk)}
                              title={t('kioskMenuPage.table.actions')}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* 📱 MOBİL: Kart Görünümü */}
              {isMobile && (
                <Box>
                  {paginatedKiosks.map((kiosk) => (
                    <Paper 
                      key={kiosk.id} 
                      elevation={2} 
                      sx={{ 
                        mb: 1.5, 
                        borderRadius: 2, 
                        overflow: 'hidden',
                        border: '1px solid',
                        borderColor: kiosk.status === 'active' ? 'success.light' : 
                                    kiosk.status === 'maintenance' ? 'warning.light' : 'error.light',
                      }}
                    >
                      {/* Top: kiosk name + status + menu */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          p: 1.5, 
                          bgcolor: 'grey.50',
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <ComputerIcon sx={{ 
                            fontSize: 28, 
                            color: theme.palette.primary.main
                          }} />
                          <Box>
                            <Typography variant="body1" fontWeight="bold">
                              {kiosk.name}
                            </Typography>
                            {kiosk.description && (
                              <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                                {kiosk.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip 
                            label={getStatusText(kiosk.status)}
                            color={getStatusColor(kiosk.status) as any}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={(e) => handleMenuOpen(e, kiosk)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      {/* Alt Kısım: Detaylar */}
                      <Box sx={{ p: 1.5 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('kioskMenuPage.table.screenSize')}</Typography>
                            <Typography variant="body2" fontWeight="medium">
                              🖥️ {getScreenSizeText(kiosk.screenSize)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('kioskMenuPage.table.branch')}</Typography>
                            <Typography variant="body2" sx={{
                              color: !kiosk.branchName || kiosk.branchName === t('kioskMenuPage.table.unassigned') ? 'text.secondary' : 'text.primary'
                            }}>
                              📍 {kiosk.branchName || t('kioskMenuPage.table.unassigned')}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('kioskMenuPage.table.deviceStatus')}</Typography>
                            <Box mt={0.5}>
                              {kiosk.deviceStatus === 'active' ? (
                                <Chip label={t('kioskMenuPage.deviceStatus.connected')} color="success" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
                              ) : kiosk.deviceStatus === 'pending' ? (
                                <Chip label={t('kioskMenuPage.deviceStatus.pendingSetup')} color="warning" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
                              ) : kiosk.deviceStatus === 'inactive' ? (
                                <Chip label={t('kioskMenuPage.deviceStatus.offline')} color="error" size="small" sx={{ fontSize: '0.7rem', height: 22 }} />
                              ) : (
                                <Chip label={t('kioskMenuPage.deviceStatus.noDevice')} variant="outlined" size="small" sx={{ fontSize: '0.7rem', height: 22, color: 'text.secondary' }} />
                              )}
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
              
              {/* Sayfalama */}
              {kiosks.length > 0 && (
                <Paper sx={{ mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={kiosks.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={isMobile ? [10, 25, 50] : [10, 25, 50, 100]}
                    labelRowsPerPage={isMobile ? t('kioskMenuPage.pagination.page') : t('kioskMenuPage.pagination.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) => 
                      isMobile
                        ? `${from}-${to}/${count}`
                        : t('kioskMenuPage.pagination.displayedRows', { count, from, to })
                    }
                  />
                </Paper>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleEditFromMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText primary={t('kioskMenuPage.rowActions.edit')} />
        </MenuItem>
        
        <MenuItem onClick={handleSettingsFromMenu}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="warning" />
          </ListItemIcon>
          <ListItemText primary={t('kioskMenuPage.rowActions.settings')} />
        </MenuItem>
        
        <MenuItem onClick={handleRestartFromMenu}>
          <ListItemIcon>
            <RestartAltIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('kioskMenuPage.rowActions.restartDevices')} />
        </MenuItem>
        
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('kioskMenuPage.rowActions.delete')} />
        </MenuItem>
      </Menu>

      {/* Add/Edit Kiosk Modal */}
      <AddKioskModal
        open={addKioskModalOpen}
        onClose={() => {
          setAddKioskModalOpen(false);
          setEditingKiosk(null);
        }}
        onKioskAdded={handleKioskAdded}
        editKiosk={editingKiosk}
      />

      {/* Kiosk Settings Modal */}
      <KioskSettingsModal
        open={settingsModalOpen}
        onClose={() => {
          setSettingsModalOpen(false);
          setSettingsKiosk(null);
        }}
        kioskId={settingsKiosk?.id || null}
        companyId={companyId}
        kioskName={settingsKiosk?.name}
        onImageUpload={handleImageUpload}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle>{t('kioskMenuPage.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('kioskMenuPage.deleteDialog.description', { name: selectedKiosk?.name || '' })}
            <br />
            <br />
            {t('kioskMenuPage.deleteDialog.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            {t('kioskMenuPage.actions.cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
            {t('kioskMenuPage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restart Confirmation Dialog */}
      <Dialog
        open={restartDialogOpen}
        onClose={handleRestartCancel}
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestartAltIcon color="info" />
          {t('kioskMenuPage.restartDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('kioskMenuPage.restartDialog.description', { name: selectedKiosk?.name || '' })}
            <br />
            <br />
            {t('kioskMenuPage.restartDialog.warning')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRestartCancel} color="inherit">
            {t('kioskMenuPage.actions.cancel')}
          </Button>
          <Button onClick={handleRestartConfirm} color="info" variant="contained" startIcon={<RestartAltIcon />} autoFocus>
            {t('kioskMenuPage.actions.restart')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default KioskMenuPage;
